// ============================================================================
// useMessageSelection.ts — режим МНОЖЕСТВЕННОГО ВЫБОРА сообщений чата
// (§2.3 паритета: копировать/удалить/переслать). Вход — «Выбрать» в
// контекстном меню сообщения (ПКМ или кнопка «…»). В режиме тап по баблу
// выбирает/снимает; выход — «Отмена»/Esc/клик вне сообщений.
//
// УДЕРЖАНИЕ 500 мс (тач/перо) открывает КОНТЕКСТНОЕ МЕНЮ сообщения (если
// передан onLongPress) — как в мессенджерах: ответить/переслать/изменить/
// удалить/выбрать. Без обработчика удержание по-прежнему входит в выбор.
//
// Жесты навешиваются ДЕЛЕГИРОВАНИЕМ на ленту (data-msg-bubble/data-msg-id) —
// memo-баблы MessageBubble не получают лишних обработчиков и не пересоздаются.
// ============================================================================

import { useCallback, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

/** id сообщения под указателем (делегирование; '' — вне баблов). */
export function messageIdAt(target: EventTarget | null): string {
  const el = (target as HTMLElement | null)?.closest?.('[data-msg-bubble]') as HTMLElement | null;
  return el?.getAttribute('data-msg-id') || '';
}

export function useMessageSelection(
  /** Удержание 500 мс (тач/перо) — открыть контекстное меню сообщения. */
  onLongPress?: (id: string, x: number, y: number) => void,
) {
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Гонка «удержание → click»: сразу после срабатывания удержания браузер
  // шлёт click — он НЕ должен снять только что выбранное сообщение
  // (подавляем ОДИН клик по тому же сообщению; чужие клики не трогаем).
  const longPressAt = useRef(0);
  const longPressId = useRef('');
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressStart = useRef<{ x: number; y: number } | null>(null);

  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelectedIds([]);
  }, []);

  const enterSelect = useCallback((id?: string) => {
    setSelectMode(true);
    if (id) setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const toggleSelect = useCallback((id: string) => {
    if (longPressId.current === id && Date.now() - longPressAt.current < 800) {
      longPressId.current = '';
      return;
    }
    longPressId.current = '';
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const clearPress = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    pressStart.current = null;
  }, []);

  // Удержание 500 мс — только тач/перо (мышь — ПКМ → «Выбрать»), не ломает
  // скролл: сдвиг указателя отменяет удержание (onPointerMove).
  const onPointerDown = useCallback((e: ReactPointerEvent) => {
    if (selectMode || e.pointerType === 'mouse') return;
    const id = messageIdAt(e.target);
    if (!id) return;
    pressStart.current = { x: e.clientX, y: e.clientY };
    pressTimer.current = setTimeout(() => {
      pressTimer.current = null;
      // Пользователь ВЫДЕЛЯЕТ ТЕКСТ (штатное выделение) — жест не перехватываем
      if (window.getSelection()?.toString()) return;
      longPressAt.current = Date.now();
      longPressId.current = id;
      if (onLongPress) {
        onLongPress(id, pressStart.current?.x ?? 0, pressStart.current?.y ?? 0);
        pressStart.current = null;
        return;
      }
      enterSelect(id);
    }, 500);
  }, [selectMode, enterSelect, onLongPress]);

  const onPointerMove = useCallback((e: ReactPointerEvent) => {
    const p = pressStart.current;
    if (!p) return;
    if (Math.abs(e.clientX - p.x) > 8 || Math.abs(e.clientY - p.y) > 8) clearPress();
  }, [clearPress]);

  // Клик вне сообщений (фон ленты) — выход; кнопки интерфейса не считаются «вне».
  const onClickOutside = useCallback((e: ReactMouseEvent) => {
    if (!selectMode) return;
    const t = e.target as HTMLElement;
    if (t.closest('[data-msg-bubble]') || t.closest('button')) return;
    exitSelect();
  }, [selectMode, exitSelect]);

  return {
    selectMode,
    selectedIds,
    enterSelect,
    exitSelect,
    toggleSelect,
    onPointerDown,
    onPointerMove,
    onPointerUp: clearPress,
    onPointerCancel: clearPress,
    onClickOutside,
  };
}
